<template>
  <KiFormDialog
    title="编辑图片"
    :show="show"
    append-to-body
    destroy-on-close
    center
    v-on="$listeners"
    @close="$emit('update:show', false)"
    @fullscreen-change="onFullscreenChange"
  >
    <div v-loading="loading" element-loading-text="图片加载中...">
      <div :style="{ height: `${fullscreen ? '747' : '500'}px`, position: 'relative' }" flex justify-center items-center>
        <img ref="cropper" display="block" max-w="full" max-h="full" :src="localURL" />
        <div class="resolution" v-if="resolution.width != undefined">{{ resolution.width }}&times;{{ resolution.height }}</div>
      </div>

      <div flex flex-col items-center mt-25px gap-10px>
        <el-button-group>
          <el-button icon="el-icon-zoom-in" title="放大" @click.prevent="zoom(0.2)" />
          <el-button icon="el-icon-zoom-out" title="缩小" @click.prevent="zoom(-0.2)" />
          <el-button icon="el-icon-arrow-left" title="左移" @click.prevent="move(-10, 0)" />
          <el-button icon="el-icon-arrow-right" title="右移" @click.prevent="move(10, 0)" />
          <el-button icon="el-icon-arrow-up" title="上移" @click.prevent="move(0, -10)" />
          <el-button icon="el-icon-arrow-down" title="下移" @click.prevent="move(0, 10)" />
          <el-button ref="flipX" title="左右翻转" class="flipX" icon="el-icon-sort" @click.prevent="flipX" />
          <el-button ref="flipY" title="上下翻转" icon="el-icon-sort" @click.prevent="flipY" />
          <el-button icon="el-icon-refresh-right" title="右旋转90°" @click.prevent="rotate(90)" />
          <el-button icon="el-icon-refresh-left" title="左旋转90°" @click.prevent="rotate(-90)" />
          <template v-if="aspectRatio && aspectRatio.target">
            <el-button v-if="cancelAspectRatio" icon="el-icon-lock" title="限制裁切比例" @click.prevent="cancelRatio()" />
            <el-button v-else icon="el-icon-unlock" title="取消裁切比例限制" @click.prevent="cancelRatio()" />
          </template>
          <!-- <el-button type="info" @click.prevent="reset" icon="el-icon-refresh"/> -->
        </el-button-group>

        <el-slider
          v-model="rotateDegree"
          :style="{ visibility: loading ? 'hidden' : 'visible' }"
          class="rotateDegree"
          show-input
          :debounce="Debounce"
          :min="-180"
          :max="180"
          :marks="{ '0': '0°', '-180': '-180°', '180': '180°' }"
          @change="() => { isTouched = true }"
        />
      </div>
    </div>

    <template #footer>
      <div flex items-center justify-between>
        <div class="spill-text">
          <template v-if="isCompress && resolution && resolution.width > compressMaxWidth">
            <span class="text">已开启自动压缩</span>
            <el-tooltip effect="dark" placement="top">
              <template #content>
                原图片宽度过大，可能会影响加载速度<br />
                已开启自动压缩，压缩后图片宽度将会限制在{{ compressMaxWidth }}px<br />
                可能会影响图片清晰度，如果对图片清晰度要求较高，请关闭自动压缩
              </template>
              <i class="el-icon-question mr-5px" />
            </el-tooltip>
            <el-switch v-model="userCompress" @change="getEditFile()" :active-value="true" :inactive-value="false" />
          </template>
        </div>
        <div flex items-center justify-end>
          <el-form v-show="!loading" inline flex class="!mb-0">

            <!-- <el-form-item class="!mb-0">
              <template #label>
                图片品质
                <el-tooltip effect="dark" placement="top">
                  <template #content>
                    调低该值可缩小图片体积<br />
                    调高该值可提升清晰度
                  </template>
                  <i class="el-icon-question" />
                </el-tooltip>
              </template>
              <el-slider
                v-model="quality"
                class="quality"
                :min="0"
                :max="1"
                :step="0.1"
                size="mini"
              />
            </el-form-item> -->
          </el-form>

          <div v-show="!loading && !MustCrop">
            <el-tag>原图{{ sizeText }}</el-tag>
          </div>

          <el-button :disabled="loading" style="margin-left: 10px" type="info" @click="reset">重 置</el-button>
          <el-button @click="$emit('cancel')">取 消</el-button>
          <el-button :disabled="loading" type="primary" :loading="submitting" @click="onConfirm">确 定</el-button>
        </div>
      </div>
    </template>
  </KiFormDialog>
</template>

<script>
import 'cropperjs/dist/cropper.min.css'
import Cropper from 'cropperjs'
import { KiFormDialog } from 'kikimore'
import SwalPreset from 'sweetalert2-preset'
import { throttle } from 'lodash-es'
import Compressor from 'compressorjs'
import { KB, MB, blobToFile, sizeToText, toBinary, toImageTag, toLocalURL } from '../utils'
import { reactive } from 'vue'

const initialSettings = () => ({
  sizeThreshold: MB,
  rotateDegree: 0,
  quality: 0.8,
  isTouched: false
})

const initialState = () => ({
  ...initialSettings(),
  fullscreen: false,
  binary: null,
  localURL: null,
  imageTag: null,
  cropper: null,
  loading: true,
  submitting: false,
  sizeText: '',
  canvas: {},
  cropBox: {},
  cancelAspectRatio: false, // 取消比例限制
  resolution: {},
  timer: null,
  userCompress: false // 用户可以自己设置的是否压缩
})

export default {
  components: { KiFormDialog },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    value: [Blob, File, String],
    aspectRatio: Object,
    // compressorjs压缩参数
    compressorParams: {
      type: Object,
      default: () => ({})
    },
    CropperMode: 0,
    // 是否压缩
    isCompress: Boolean,
    compressMaxWidth: Number
  },
  data() {
    return initialState()
  },
  computed: {
    Debounce() {
      return this.binary ? Math.min(500, Math.round(this.binary.size / KB)) : 300
    },
    MustCrop() {
      const { width, height } = this.imageTag
      const originalAspectRatio = width / height

      return (
        this.isTouched ||
        (this.aspectRatio.min !== undefined && originalAspectRatio < this.aspectRatio.min) ||
        (this.aspectRatio.max !== undefined && originalAspectRatio > this.aspectRatio.max)
      )
    }
  },
  watch: {
    // 监听 value 不监听 show 是为了兼顾多选的情况
    async value(newValue) {
      this.userCompress = true // 默认打开
      if (newValue) {
        this.loading = true

        try {
          this.binary = await toBinary(newValue)
          this.localURL = await toLocalURL(newValue)
          this.imageTag = await toImageTag(this.localURL)
        } catch (e) {
          SwalPreset.error(e instanceof Error ? e.message : e)
          this.$emit('update:show', false)
          return
        }

        await this.$nextTick()
        const _this = this
        this.cropper ??= new Cropper(this.$refs.cropper, {
          'overflow-hidden': true,
          preview: '.preview',
          background: true,
          cropmove: () => {
            this.isTouched = true
          },
          zoom: () => {
            this.isTouched = true
          },
          ready: this.onReady,
          viewMode: this.CropperMode,
          crop({ detail: e }) {
            // 获取裁剪后的经纬度
            if(e.width && e.height) {
              _this.resolution = {
                width: Math.floor(e.width),
                height: Math.floor(e.height)
              }
            } else {
              _this.resolution = {}
            }

            // 获取裁剪后的文件，并压缩
            // _this.getEditFile()
          }
        })

        // 大图会卡，加一个节流
        this.updateCropBox = throttle(
          (arg) => {
            this.cropper.rotateTo(arg)
            this.onReady()
          },
          this.Debounce,
          {
            leading: false,
            trailing: true
          }
        )

        this.sizeText = sizeToText(this.binary.size)

        this.cropper.replace(this.localURL) // replace 后触发 onReady（参数为 Base64 类型才会触发）
      } else {
        Object.assign(this.$data, initialState())
      }
    },
    rotateDegree(n) {
      this.onRotateDegreeChange(n)
    }
  },
  mounted() {
    // fix: 在 webpack 项目中会报错，但不影响正常使用
    try {
      useEventListener(
        window,
        'resize',
        throttle(
          () => {
            if (this.show && !this.loading && !this.submitting) {
              this.reset()
            }
          },
          100,
          {
            leading: false,
            trailing: true
          }
        )
      )

      useEventListener(document, 'keydown', (e) => {
        if (this.show && !this.loading && !this.submitting) {
          if (e.key === 'Enter') {
            this.onConfirm()
          }
        }
      })
    } catch (e) {}
  },
  methods: {
    getEditFile() {
      return new Promise((resolve) => {
        this.cropper.getCroppedCanvas().toBlob(
          (blob) => {
            // 如果输入为 File 类型，则输出也应为 File 类型，避免 name 丢失
            const binary = this.binary instanceof File ? blobToFile(blob, this.binary.name) : blob

            /**
             * isCompress 配置了自动压缩压缩
             * userCompress 用户开启了压缩
             * compressMaxWidth resolution 超出了设置的最大宽度
             * 才需要压缩
            */
            if(
              this.isCompress &&
              this.userCompress &&
              this.compressMaxWidth &&
              this.resolution &&
              this.resolution.width > this.compressMaxWidth) {
              // 超出限制自动压缩
              this.submitting = true
              this.compressor(binary, this.compressMaxWidth).then((file) => {
                resolve(file)
              })
            } else {
              resolve(binary)
            }
          },
          // 如果旋转角度不为直角，则图片一定会出现空白区域，空白区域默认透明，使用 png 格式
          // this.rotateDegree % 90 === 0 ? this.binary.type : 'image/png',
          this.binary.type,
          // 质量
          1
        )
      })
    },
    onFullscreenChange(v) {
      this.fullscreen = v
      this.$nextTick(() => {
        window.dispatchEvent(new Event('resize'))
        this.reset()
      })
    },
    onRotateDegreeChange(n) {
      this.updateCropBox(n)
    },
    onConfirm() {
      if (!this.MustCrop && !this.userCompress) {
        // 如果没有裁剪，且用户设置不压缩，直接上传原图片
        this.$emit('confirm', this.value)
      } else {
        this.submitting = true
        const { width, height } = this.cropper.getCropBoxData()
        const aspectRatio = width / height
        if (
          ((this.aspectRatio.min !== undefined && aspectRatio < this.aspectRatio.min) ||
            (this.aspectRatio.max !== undefined && aspectRatio > this.aspectRatio.max)) &&
          !this.cancelAspectRatio
        ) {
          SwalPreset.warning(`不符合${this.aspectRatio.label}`)
          this.submitting = false
          return
        }

        this.getEditFile().then((file) => { 
          this.$emit('confirm', file)
        }).finally(() => {
          this.submitting = false
        })
      }
    },
    // 压缩图片
    compressor(file, maxWidth = undefined) {
      return new Promise((resolve, reject) => { 
        new Compressor(file, {
          quality: this.quality,
          convertTypes: [],
          convertSize: Infinity,
          strict: true, // 严格模式（精确尺寸）
          orient: true, // 保持EXIF方向信息
          checkOrientation: true, // 检查EXIF方向
          ...this.compressorParams,
          maxWidth,
          success(result) {
            const resultFile = blobToFile(result, result.name)
            // console.log('源文件大小', file.size / MB)
            // console.log('压缩后文件大小', result.size / MB)
            resolve(resultFile)
          },
          error(err) {
            resolve(file) // 压缩失败，直接返回原图片
          }
        })
      })
    },
    onReady() {
      const { width, height, left, top } = this.cropper.getCanvasData()
      // this.canvas = { width, height, left, top }
      if (typeof this.aspectRatio.target === 'number') {
        this.cropper.setAspectRatio(this.aspectRatio.target)

        this.$nextTick(() => {
          // 默认裁剪框在图片之内（避免裁剪出白边），也可以放大以完全框住图片（避免遗漏信息）
          const originalRatio = width / height
          // this.cropBox = this.cropper.getCropBoxData()
          if (this.aspectRatio.target > originalRatio) {
            this.cropper.setCropBoxData({ width, left })
            const { width: containerWidth, height: containerHeight } =
              this.cropper.getContainerData()
            const { width: cropBoxWidth, height: cropBoxHeight } = this.cropper.getCropBoxData() // 不能提前拿
            this.cropper.setCropBoxData({ top: (containerHeight - cropBoxHeight) / 2 })
          } else {
            this.cropper.setCropBoxData({ height, top })
            const { width: containerWidth, height: containerHeight } =
              this.cropper.getContainerData()
            const { width: cropBoxWidth, height: cropBoxHeight } = this.cropper.getCropBoxData() // 不能提前拿
            this.cropper.setCropBoxData({ left: (containerWidth - cropBoxWidth) / 2 })
          }
          this.loading = false
        })
      } else {
        // this.cropBox = { ...this.canvas }
        this.cropper.setCropBoxData({ width, height, left, top })
        this.loading = false
      }
    },
    flipX() {
      this.isTouched = true
      const dom = this.$refs.flipX.$el
      let scale = dom.getAttribute('data-scale')
      scale = scale ? -scale : -1
      this.cropper.scaleX(scale)
      dom.setAttribute('data-scale', scale)
    },
    flipY() {
      this.isTouched = true
      const dom = this.$refs.flipY.$el
      let scale = dom.getAttribute('data-scale')
      scale = scale ? -scale : -1
      this.cropper.scaleY(scale)
      dom.setAttribute('data-scale', scale)
    },
    getCropBoxData() {
      this.data = JSON.stringify(this.cropper.getCropBoxData(), null, 4)
    },
    getData() {
      this.data = JSON.stringify(this.cropper.getData(), null, 4)
    },
    move(offsetX, offsetY) {
      this.isTouched = true
      this.cropper.move(offsetX, offsetY)
    },
    reset() {
      Object.assign(this.$data, initialSettings())
      this.cropper.reset()
      this.onReady()
      this.isTouched = false
    },
    rotate(deg) {
      this.isTouched = true
      const sum = this.rotateDegree + deg
      if (sum > 180) {
        this.rotateDegree = sum - 360
      } else if (sum < -180) {
        this.rotateDegree = sum + 360
      } else {
        this.rotateDegree = sum
      }
      this.onRotateDegreeChange(this.rotateDegree)
      // this.cropper.rotate(deg)
    },
    setCropBoxData() {
      if (!this.data) return
      this.cropper.setCropBoxData(JSON.parse(this.data))
    },
    zoom(percent) {
      this.cropper.zoom(percent)
    },
    // 取消比例限制
    cancelRatio() {
      this.cancelAspectRatio = !this.cancelAspectRatio
      this.cropper.setAspectRatio(this.cancelAspectRatio ? NaN : this.aspectRatio.target)
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.flipX > .el-icon-sort) {
  transform: rotate(90deg);
}

:deep(.cropper-point) {
  width: 8px !important;
  height: 8px !important;
  border-radius: 50%;
}

:deep(.el-dialog__footer) {
  width: 100%;
}

.rotateDegree.el-slider {
  width: 551px;

  :deep(.el-slider__marks-text:last-child) {
    width: 36.406px;
  }
}

.resolution {
  position: absolute;
  right: 0;
  bottom: 0;
  background-color: rgba($color: #000000, $alpha: 0.3);
  color: rgba($color: #fff, $alpha: 0.8);
  padding: 3px 6px;
  border-radius: 5px 0 0 0;
  font-size: 13px;
  user-select: none;
  pointer-events: none;
}

.spill-text {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  .text {
    line-height: 1;
    margin-right: 4px;
  }
}

.quality.el-slider {
  width: 75px;
}

:deep(.el-form-item__label-wrap) {
  margin-left: unset !important;
}

.el-form-item {
  margin-bottom: unset;
}

:deep(.cropper-hidden) {
  display: none !important;
  max-height: 100% !important;
}
</style>
